<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.societies.title') }}
          </h1>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">
        <div class="card card-flush">
          <div class="card-header border-0 pt-6">
            <div class="card-title">
              <div class="d-flex align-items-center position-relative my-1">
                <i class="fas fa-search position-absolute ms-4 fs-6"></i>
                <input type="text" v-model="search" @input="onSearch"
                       class="form-control form-control-solid w-250px ps-12"
                       :placeholder="$t('landlord.societies.search_placeholder')" />
              </div>
            </div>
          </div>

          <div class="card-body py-4">
            <div v-if="loading" class="text-center py-10">
              <span class="spinner-border text-primary"></span>
            </div>
            <div v-else-if="!societies.length" class="text-center py-10 text-gray-500">
              {{ $t('landlord.societies.no_results') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                <thead>
                  <tr class="fw-bolder text-muted">
                    <th>{{ $t('landlord.societies.col_name') }}</th>
                    <th>{{ $t('landlord.societies.col_owner') }}</th>
                    <th>{{ $t('landlord.societies.col_plan') }}</th>
                    <th>{{ $t('landlord.societies.col_shops_count') }}</th>
                    <th>{{ $t('landlord.societies.col_date') }}</th>
                    <th class="text-end">{{ $t('landlord.societies.col_actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="society in societies" :key="society.id">
                    <td>
                      <router-link :to="`/landlord/societies/${society.id}`" class="text-dark fw-bold text-hover-primary">
                        {{ society.name || society.id }}
                      </router-link>
                    </td>
                    <td>
                      <span v-if="society.owner">{{ society.owner.name }}</span>
                      <span v-else class="text-gray-400">{{ $t('landlord.societies.no_owner') }}</span>
                    </td>
                    <td>
                      <span v-if="society.plan" class="badge badge-light-primary">{{ society.plan }}</span>
                      <span v-else class="text-gray-400">-</span>
                    </td>
                    <td>{{ society.shops_count }}</td>
                    <td class="text-gray-500">{{ formatDate(society.created_at) }}</td>
                    <td class="text-end">
                      <router-link :to="`/landlord/societies/${society.id}`" class="btn btn-icon btn-sm btn-light-primary" :title="$t('landlord.societies.view')">
                        <i class="fas fa-eye"></i>
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="meta && meta.last_page > 1" class="d-flex justify-content-between align-items-center mt-5">
              <div class="text-gray-500 fs-7">
                {{ $t('landlord.shops.showing') }} {{ meta.from }} - {{ meta.to }} / {{ meta.total }}
              </div>
              <ul class="pagination">
                <li class="page-item" :class="{ disabled: meta.current_page <= 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(meta.current_page - 1)">
                    <i class="fas fa-chevron-left fs-7"></i>
                  </a>
                </li>
                <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === meta.current_page }">
                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: meta.current_page >= meta.last_page }">
                  <a class="page-link" href="#" @click.prevent="goToPage(meta.current_page + 1)">
                    <i class="fas fa-chevron-right fs-7"></i>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'LandlordSocietiesIndex',
    data() {
        return {
            search: '',
            loading: false,
            searchTimeout: null,
            currentPage: 1,
        }
    },
    computed: {
        ...mapGetters('landlord', { societies: 'societies', meta: 'societiesMeta' }),

        visiblePages() {
            if (!this.meta) return []
            const pages = []
            const start = Math.max(1, this.meta.current_page - 2)
            const end = Math.min(this.meta.last_page, this.meta.current_page + 2)
            for (let i = start; i <= end; i++) {
                pages.push(i)
            }
            return pages
        }
    },
    mounted() {
        this.loadSocieties()
    },
    methods: {
        ...mapActions('landlord', ['fetchSocieties']),

        loadSocieties() {
            this.loading = true
            this.fetchSocieties({ page: this.currentPage, search: this.search, per_page: 15 })
                .finally(() => { this.loading = false })
        },

        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.loadSocieties()
            }, 400)
        },

        goToPage(page) {
            if (page < 1 || (this.meta && page > this.meta.last_page)) return
            this.currentPage = page
            this.loadSocieties()
        },

        formatDate(dateStr) {
            if (!dateStr) return '-'
            const d = new Date(dateStr)
            return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        }
    }
}
</script>
